import {BrowserModule} from '@angular/platform-browser';
import {Component, ErrorHandler, LOCALE_ID, NgModule, NgZone, Type} from '@angular/core';
import localeDE from '@angular/common/locales/de';
import {AppComponent} from './app.component';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {UserServiceService} from "./service/user-service.service";
import {NavbarComponent} from './component/navbar/navbar.component';
import {LoginPageComponent} from './component/login/login-page/login-page.component';
import {LoginFormComponent} from './component/login/login-form/login-form.component';
import {HomePageComponent} from './component/home/home-page/home-page.component';
import {DashboardComponent} from './component/home/dashboard/dashboard.component';
import {HTTP_INTERCEPTORS, HttpClientModule} from "@angular/common/http";
import {Router, RouterModule, Routes} from "@angular/router";
import {AuthGuard} from "./guards/auth.guard";
import {CookieService} from "ngx-cookie-service";
import {OrderService} from "./service/order.service";
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {ProductsContainerComponent} from './component/new-order/products-container/products-container.component';
import {NewOrderPageComponent} from './component/new-order/new-order-page/new-order-page.component';
import {ProductService} from "./service/product.service";
import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {PropertyFormComponent} from './component/new-order/property-form/property-form.component';
import {ButtonComponent} from './component/button/button.component';
import {ModalComponent} from './component/modal/modal.component';
import {NotificationComponent} from './component/notification/notification.component';
import {AuthorityPageComponent} from './component/authority/authority-page/authority-page.component';
import {DropzoneComponent} from './component/dropzone/dropzone.component';
import {DropzoneDirective} from './directives/dropzone.directive';
import {AuthorityPreviewComponent} from './component/authority/authority-preview/authority-preview.component';
import {SignaturePadComponent} from './component/signature-pad/signature-pad.component';
import {PackageComponent} from './component/new-order/package/package.component';
import {PackageContainerComponent} from './component/new-order/package-container/package-container.component';
import {SummaryPageComponent} from './component/new-order/summary-page/summary-page.component';
import {RunScriptsDirective} from './directives/run-scripts.directive';
import {SafeHtmlPipe} from './pipes/safe-html.pipe';
import {InputComponent} from './component/input/input.component';
import {ValidateInputDirective} from './directives/validate-input.directive';
import {ConfirmationPageComponent} from './component/new-order/confirmation-page/confirmation-page.component';
import {registerLocaleData} from "@angular/common";
import {Interceptor} from "./interceptor";
import {ContactOverlayComponent} from './component/contact-overlay/contact-overlay.component';
import {RegisterPageComponent} from './component/register/register-page/register-page.component';
import {SpinnerOverlayComponent} from './component/spinner-overlay/spinner-overlay.component';
import {FooterComponent} from "./component/footer/footer.component";
import {SuccessPageComponent} from './component/register/success-page/success-page.component';
import {ProgressBarComponent} from './component/new-order/progress-bar/progress-bar.component';

import {PackageMobileComponent} from './component/new-order/package-mobile/package-mobile.component';
import {
  ProductsContainerMobileComponent
} from './component/new-order/products-container-mobile/products-container-mobile.component';
import {LegitimateInterestPipe} from "./pipes/legitimate-interest.pipe";
import {NewOrderMobileComponent} from "./component/new-order/new-order-mobile/new-order-mobile.component";

import {OrderComponent} from './component/orders/order/order.component';
import {OderDetailsComponent} from './component/orders/oder-details/oder-details.component';
import {OrderObjectComponent} from './component/orders/order-object/order-object.component';
import {FilterUniquePipe} from './pipes/filter-unique.pipe';

import {MatDialogModule} from "@angular/material/dialog";
import {SubauthorityPreviewComponent} from './component/authority/subauthority-preview/subauthority-preview.component';
import {PlatformService} from "./service/platform.service";
import {
  NewOrderPageConnectComponent
} from './component/new-order/new-order-page-connect/new-order-page-connect.component';
import {DropzoneConnectComponent} from './component/dropzone-connect/dropzone-connect.component';
import {SearchPipe} from './pipes/search.pipe';
import {PackageService} from "./service/package.service";
import {NotFoundComponent} from './component/not-found/not-found.component';
import {DocumentStatusService} from "./service/document-status.service";
import {getErrorHandler} from "./SentryErrorHandler";
import {MatSnackBarModule} from "@angular/material/snack-bar";
import {ErrorComponent} from './component/error/error.component';
import {ErrorService} from "./service/error.service";
import {AccountComponent} from './component/account/account.component';
import {UrlService} from "./service/url.service";
import {PropertyInformationComponent} from './component/new-order/property-information/property-information.component';
import {FormContainerComponent} from './component/form-container/form-container.component';
import {RegisterFormComponent} from './component/register/register-form/register-form.component';
import {DownloadPageComponent} from './component/downloads/download-page/download-page.component';
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatPaginatorIntl, MatPaginatorModule} from "@angular/material/paginator";
import {PaginationComponent} from './component/pagination/pagination.component';
import {FilterPipePipe} from "./pipes/filter-pipe.pipe";
import {DocumentRowComponent} from "./component/orders/document-row/document-row.component";
import {NgxSkeletonLoaderModule} from "ngx-skeleton-loader";
import {SavedPropertiesComponent} from './component/saved-properties/saved-properties/saved-properties.component';
import {
  SavedPropertyDetailsComponent
} from './component/saved-properties/saved-property-details/saved-property-details.component';
import {
  DocumentOverviewLastOrderComponent
} from './component/saved-properties/document-overview-last-order/document-overview-last-order.component';
import {DeactivateGuard} from "./guards/deactivate.guard";
import {FileExplorerPositionComponent} from "./component/file-explorer-position/file-explorer-position.component";
import {DownloadListComponent} from './component/downloads/download-list/download-list.component';
import {CustomerInteractionComponent} from './component/orders/customer-interaction/customer-interaction.component';
import {DBConfig, NgxIndexedDBModule} from "ngx-indexed-db";
import {NgxPaginationModule} from 'ngx-pagination';
import {BatchDownloadComponent} from './component/downloads/batch-download/batch-download/batch-download.component';
import {DownloadModalComponent} from './component/orders/download-modal/download-modal.component';
import {TextFieldModule} from "@angular/cdk/text-field";
import {TooltipDirective} from './directives/tooltip.directive';
import {OrderPermissionsComponent} from './component/orders/order-permissions/order-permissions.component';
import {MatIconModule} from "@angular/material/icon";
import {
  EditUserGroupDialogComponent
} from './component/dialogs/edit-user-group-dialog/edit-user-group-dialog.component';
import {MatTabsModule} from "@angular/material/tabs";
import {MatCardModule} from "@angular/material/card";
import {MatButtonModule} from "@angular/material/button";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatChipsModule} from "@angular/material/chips";
import {OrderMenuComponent} from './component/menu/order-menu/order-menu.component';
import {MatMenuModule} from "@angular/material/menu";
import {InvitesComponent} from './component/invites/invites.component';
import {InvitesListComponent} from './component/invites/invites-list/invites-list.component';
import {UserGroupManagementComponent} from './component/user-group-management/user-group-management.component';
import {MatExpansionModule} from "@angular/material/expansion";
import {UserManagementMenuComponent} from './component/menu/user-management-menu/user-management-menu.component';
import {
  CompanyLoginSelectionComponent
} from './component/menu/company-login-selection/company-login-selection.component';
import {MoveUserDialogComponent} from './component/dialogs/move-user-dialog/move-user-dialog.component';
import {ConfirmationDialogComponent} from './component/dialogs/confirmation-dialog/confirmation-dialog.component';
import {AddUserDialogComponent} from './component/dialogs/add-user-dialog/add-user-dialog.component';
import {BasicSnackbarComponent} from './component/snackbars/basic-snackbar/basic-snackbar.component';
import {LoginIdService} from "./service/login-id.service";
import {ServiceRendererComponent} from './component/service-renderer/service-renderer.component';
import {
  BillingAddressDialogComponent
} from './component/dialogs/billing-address-dialog/billing-address-dialog.component';
import {
  PortfolioManagementComponent
} from './component/saved-properties/saved-properties/portfolio-management/portfolio-management.component';
import {
  RotationOrderManagementComponent
} from './component/saved-properties/saved-properties/rotation-order-management/rotation-order-management.component';
import {MatInputModule} from "@angular/material/input";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatNativeDateModule} from "@angular/material/core";
import {TwoDigitNumberPipe} from './pipes/two-digit-number.pipe';
import {UniqueRotationOrdersPipe} from './pipes/unique-rotation-orders.pipe';
import {PlanungDocumentRowComponent} from "./component/orders/planung-document-row/planung-document-row.component";
import {NgCircleProgressModule} from "ng-circle-progress";
import {OwnerResearchComponent} from './component/orders/owner-research/owner-research.component';
import {getGermanPaginatorIntl} from "./utils/custom-paginator-intl";
import {OAuthModule} from "angular-oauth2-oidc";
import {AuthService} from "./service/auth.service";
import { StatusMessageComponent } from './component/orders/status-message/status-message.component';
import { CustomTooltipDirective } from './directives/custom-tooltip.directive';
import { CustomTooltipComponent } from './component/custom-tooltip/custom-tooltip.component';
import { InformationDialogComponent } from './component/dialogs/information-dialog/information-dialog.component';
import { GrundeInformationModalComponent } from './component/modal/specific-modals/grunde-information-modal/grunde-information-modal.component';
import { ProductValueFormComponent } from './component/new-order/products-container/product-value-form/product-value-form.component';
import { KampfmInformationComponent } from './component/new-order/information-components/kampfm-information/kampfm-information.component';
import { TeilunInformationComponent } from './component/new-order/information-components/teilun-information/teilun-information.component';
import { ResearchDepartmentInformationComponent } from './component/new-order/information-components/research-department-information/research-department-information.component';
import { BaulasInformationComponent } from './component/new-order/information-components/baulas-information/baulas-information.component';
import { ParcelMapComponent } from './component/new-order/parcel-map/parcel-map.component';
import { ObjectOwnerComponent } from './component/object-owner/object-owner.component';
import { FolioNumberComponent } from './component/folio-number/folio-number.component';
import { BewilInformationComponent } from './component/new-order/information-components/bewil-information/bewil-information.component';
import { InformationPopupComponent } from './component/information-popup/information-popup.component';
import { AuthorityInformationComponent } from './component/authority/authority-information/authority-information.component';
import { LegitimateInterestSelectorComponent } from './component/authority/legitimate-interest-selector/legitimate-interest-selector.component';
import { BauakModalComponent } from './component/new-order/bauak-components/bauak-modal/bauak-modal.component';
import { GrundakteModalComponent } from './component/new-order/bewil-components/bewil-modal/grundakte-modal.component';


const dbConfig: DBConfig = {
  name: 'DocEstateDB',
  version: 5,
  objectStoresMeta: [
    {
      store: 'orders',
      storeConfig: {keyPath: 'orderid', autoIncrement: true},
      storeSchema: [
        {name: 'name', keypath: 'name', options: {unique: false}},
      ]
    },
    {
      store: 'authorities',
      storeConfig: {keyPath: 'authorityId', autoIncrement: true},
      storeSchema: [
        { name: 'file', keypath: 'file', options: { unique: false } },
        { name: 'propertyHash', keypath: 'propertyHash', options: { unique: false } },
        { name: 'type', keypath: 'type', options: { unique: false } }
      ]
    },
  ]
}

const routes: Routes = [
  {path: '', pathMatch: 'full', redirectTo: 'home'},
  {path: 'home', component: HomePageComponent, canActivate: [AuthGuard]},
  {path: 'login', redirectTo: 'home'},
  {path: 'order', component: OrderComponent, canActivate: [AuthGuard]},
  {path: 'orders', redirectTo: '/order'},
  {path: 'order_details/:orderid', component: OderDetailsComponent, canActivate: [AuthGuard]},
  {path: 'order_permissions', component: OrderPermissionsComponent, canActivate: [AuthGuard]},
  {path: 'order_object/:propertyid', component: OrderObjectComponent, canActivate: [AuthGuard]},
  {path: 'user_group_management', component: UserGroupManagementComponent, canActivate: [AuthGuard]},
  {path: 'batch-download/:downloadid', component: DownloadPageComponent, canActivate: [AuthGuard]},
  {
    path: 'customer-interaction/:customerInteractionId',
    component: CustomerInteractionComponent,
    canActivate: [AuthGuard]
  },
  {path: 'batch-download', component: DownloadListComponent, canActivate: [AuthGuard]},
  {path: 'register/success', component: SuccessPageComponent},
  {path: 'register/invite/:token', component: InvitesComponent},
  {path: 'new_order', component: getNewOrderPageComponent(), canActivate: [AuthGuard]},
  {path: 'authority', component: AuthorityPageComponent, canActivate: [AuthGuard]},
  {path: 'cart', component: SummaryPageComponent, canActivate: [AuthGuard]},
  {path: 'confirmation', component: ConfirmationPageComponent, canActivate: [AuthGuard]},
  {path: 'account', component: AccountComponent, canActivate: [AuthGuard]},
  {path: 'saved-properties', component: SavedPropertiesComponent, canActivate: [AuthGuard]},
  {
    path: 'saved-properties/:savedPropertyId',
    component: SavedPropertyDetailsComponent,
    canActivate: [AuthGuard],
    canDeactivate: [DeactivateGuard]
  },
  {
    path: 'saved-properties/new',
    component: SavedPropertyDetailsComponent,
    canActivate: [AuthGuard],
    canDeactivate: [DeactivateGuard]
  },
  {path: 'owner-research', component: OwnerResearchComponent, canActivate: [AuthGuard]},
  {path: 'not_found', component: NotFoundComponent}
];
registerLocaleData(localeDE);



@NgModule({
  declarations: [
    AppComponent,
    NavbarComponent,
    LoginPageComponent,
    FooterComponent,
    LoginFormComponent,
    HomePageComponent,
    DashboardComponent,
    ProductsContainerComponent,
    NewOrderPageComponent,
    PropertyFormComponent,
    ButtonComponent,
    ModalComponent,
    NotificationComponent,
    AuthorityPageComponent,
    DropzoneComponent,
    DropzoneDirective,
    AuthorityPreviewComponent,
    SignaturePadComponent,
    PackageComponent,
    PackageContainerComponent,
    SummaryPageComponent,
    RunScriptsDirective,
    SafeHtmlPipe,
    InputComponent,
    ValidateInputDirective,
    ConfirmationPageComponent,
    ContactOverlayComponent,
    RegisterPageComponent,
    SpinnerOverlayComponent,
    FooterComponent,
    ProgressBarComponent,
    SuccessPageComponent,
    NewOrderMobileComponent,
    PackageMobileComponent,
    ProductsContainerMobileComponent,
    FileExplorerPositionComponent,
    LegitimateInterestPipe,
    OrderComponent,
    OderDetailsComponent,
    OrderObjectComponent,
    FilterUniquePipe,
    SubauthorityPreviewComponent,
    NewOrderPageConnectComponent,
    DropzoneConnectComponent,
    SearchPipe,
    NotFoundComponent,
    ErrorComponent,
    AccountComponent,
    PropertyInformationComponent,
    FormContainerComponent,
    RegisterFormComponent,
    DownloadPageComponent,
    PaginationComponent,
    FilterPipePipe,
    DocumentRowComponent,
    SavedPropertiesComponent,
    SavedPropertyDetailsComponent,
    DocumentOverviewLastOrderComponent,
    FileExplorerPositionComponent,
    DownloadListComponent,
    CustomerInteractionComponent,
    BatchDownloadComponent,
    OrderPermissionsComponent,
    EditUserGroupDialogComponent,
    OrderMenuComponent,
    InvitesComponent,
    InvitesListComponent,
    UserGroupManagementComponent,
    UserManagementMenuComponent,
    CompanyLoginSelectionComponent,
    MoveUserDialogComponent,
    ConfirmationDialogComponent,
    AddUserDialogComponent,
    BasicSnackbarComponent,
    ServiceRendererComponent,
    BillingAddressDialogComponent,
    TooltipDirective,
    DownloadModalComponent,
    PortfolioManagementComponent,
    RotationOrderManagementComponent,
    TwoDigitNumberPipe,
    UniqueRotationOrdersPipe,
    PlanungDocumentRowComponent,
    StatusMessageComponent,
    CustomTooltipDirective,
    CustomTooltipComponent,
    OwnerResearchComponent,
    InformationDialogComponent,
    GrundeInformationModalComponent,
    ProductValueFormComponent,
    KampfmInformationComponent,
    TeilunInformationComponent,
    ResearchDepartmentInformationComponent,
    BaulasInformationComponent,
    ParcelMapComponent,
    ObjectOwnerComponent,
    FolioNumberComponent,
    BewilInformationComponent,
    InformationPopupComponent,
    AuthorityInformationComponent,
    LegitimateInterestSelectorComponent,
    BauakModalComponent,
    GrundakteModalComponent
  ],
  imports: [
    RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' }),
    MatPaginatorModule,
    BrowserModule,
    FontAwesomeModule,
    HttpClientModule,
    FormsModule,
    MatProgressSpinnerModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatPaginatorModule,
    NgxSkeletonLoaderModule,
    NgxIndexedDBModule.forRoot(dbConfig),
    OAuthModule.forRoot({
      resourceServer: {
        sendAccessToken: true
      },
    }),
    NgxPaginationModule,
    MatPaginatorModule,
    MatIconModule,
    MatTabsModule,
    MatCardModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatChipsModule,
    MatMenuModule,
    MatExpansionModule,
    TextFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgCircleProgressModule.forRoot({
      radius: 100,
      outerStrokeWidth: 16,
      innerStrokeWidth: 8,
      outerStrokeColor: "#78C000",
      innerStrokeColor: "#C7E596",
      animationDuration: 300,
    }),
  ],
  exports: [RouterModule],
  providers: [
    UserServiceService,
    LoginIdService,
    AuthGuard,
    CookieService,
    OrderService,
    ProductService,
    PlatformService,
    PackageService,
    DocumentStatusService,
    ErrorService,
    UrlService,
    SearchPipe,
    TwoDigitNumberPipe,
    AuthService,
    MatDatepickerModule,
    {
      provide: LOCALE_ID,
      useValue: 'de-DE'
    }, {
      provide: HTTP_INTERCEPTORS,
      useClass: Interceptor,
      multi: true
    }, {
      provide: ErrorHandler,
      /*useClass: SentryErrorHandler,*/
      deps: [Router, NgZone, ErrorService],
      useFactory: getErrorHandler,
    }, {
      provide: MatPaginatorIntl, useValue: getGermanPaginatorIntl()
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}

export function getNewOrderPageComponent(): Type<Component> {
  let platformProfile = JSON.parse(sessionStorage.getItem("platform"));
  if (platformProfile !== null) {
    if (platformProfile.platformType === "PROPERTY" || platformProfile.platformType === "STEUER") {
      return <Type<Component>>NewOrderPageComponent;
    } else if (platformProfile.platformType === "CONNECT" || platformProfile.platformType === "MANDATE") {
      return <Type<Component>>NewOrderPageConnectComponent;
    }
  } else {
    return <Type<Component>>NewOrderPageComponent;
  }
}
